/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
  Fragment,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from "react"
import { Transition } from "@headlessui/react"
import cx from "clsx"
import useFeatureFlag from "hooks/useFeatureFlag"
import IconButton from "components/IconButton"
import { CloseIcon } from "components/Icons"
import useBreakpoint from "hooks/useBreakpoint"
import useDesignerUser from "hooks/useDesignerUser"
import useFeatureSwitches from "hooks/useFeatureSwitches"
import {
  primaryMenuItems,
  secondaryMenuItems,
  type MenuItem,
  type MenuItemGuards
} from "lib/menuItems"
import SidebarIcons from "./SidebarIcons"
import SidebarItem from "./SidebarItem"

export interface SidebarProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  collapsed: boolean
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>
}

const filterMenuItems = (
  items: MenuItem[],
  flags: Record<MenuItemGuards, boolean>
): MenuItem[] =>
  items
    .filter(
      item =>
        !item.guards ||
        (!item.invertGuards && item.guards.find(key => flags[key])) ||
        (item.invertGuards && !item.guards.find(key => flags[key]))
    )
    .map(item => ({
      ...item,
      ...(item.items && { items: filterMenuItems(item.items, flags) })
    }))

const Sidebar = ({ open, setOpen, collapsed, setCollapsed }: SidebarProps) => {
  const { features: switches } = useFeatureSwitches()
  const { isFlagActive: isStoreSalesFlagActive } = useFeatureFlag(
    `feature_store_sales_analytics`
  )
  const { isFlagActive: isBrandsPayoutUIFlagActive } = useFeatureFlag(
    `feature_brands_payout_ui`
  )
  const { isFlagActive: isBrandApplicationUrlFlagActive } = useFeatureFlag(
    `feature_brand_application_url`
  )
  const { designerUser } = useDesignerUser()
  const flags: Record<MenuItemGuards, boolean> = useMemo(
    () => ({
      stockLevels: Boolean(switches?.featureStockLevels),
      marketingAssets: Boolean(switches?.featureBetaAssetUploadSection),
      orders: Boolean(designerUser?.designer.canAccessBetaOrdersSection),
      shippedRevenue: Boolean(
        designerUser?.designer.canAccessDesignerRevenueCsv
      ),
      communityHub:
        Boolean(designerUser?.designer.canAccessCommunityHub) &&
        !isBrandApplicationUrlFlagActive,
      communityHubNewUrl:
        Boolean(designerUser?.designer.canAccessCommunityHub) &&
        isBrandApplicationUrlFlagActive,
      returnedOrders: Boolean(
        designerUser?.designer.canAccessDesignerReturnedItemsCsv
      ),
      homePage: Boolean(designerUser?.designer.defaultToBetaDashboardHomepage),
      storeSales: Boolean(isStoreSalesFlagActive),
      brandsPayout: Boolean(isBrandsPayoutUIFlagActive)
    }),
    [
      designerUser,
      switches,
      isStoreSalesFlagActive,
      isBrandsPayoutUIFlagActive,
      isBrandApplicationUrlFlagActive
    ]
  )
  const primaryItems = useMemo(
    () => filterMenuItems(primaryMenuItems, flags),
    [flags]
  )
  const secondaryItems = useMemo(
    () => filterMenuItems(secondaryMenuItems, flags),
    [flags]
  )
  const isDesktop = useBreakpoint(`md`)
  const wasDesktopRef = useRef(isDesktop)

  const onCloseClicked: MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation()
      setOpen(false)
    },
    [setOpen]
  )

  const onToggleCollapsedClick = useCallback(() => {
    setCollapsed(current => !current)
  }, [setCollapsed])

  // This is to prevent that weird transition where resizing to mobile causes
  // the menu to suddenly pop in and immediately fade back out
  useEffect(() => {
    wasDesktopRef.current = isDesktop
  }, [isDesktop])

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (open && !(event.target as HTMLElement).closest(`#sidebar`)) {
        setOpen(false)
      }
    }

    const onEscape = (event: KeyboardEvent) => {
      if (open && event.key === `Escape`) {
        setOpen(false)
      }
    }

    const onBlur = (event: FocusEvent) => {
      if (
        open &&
        !(event.target as HTMLElement).closest(`#sidebar`) &&
        (event.target as HTMLElement).id !== `gatsby-focus-wrapper`
      ) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener(`click`, onClickOutside)
      document.addEventListener(`keydown`, onEscape)
      document.addEventListener(`focusin`, onBlur)
    }

    return () => {
      if (open) {
        document.removeEventListener(`click`, onClickOutside)
        document.removeEventListener(`keydown`, onEscape)
        document.removeEventListener(`focusin`, onBlur)
      }
    }
  }, [setOpen, open])

  return (
    <>
      <SidebarIcons />

      <Transition.Root show={open || isDesktop}>
        <div
          id="sidebar"
          data-testid="sidebar"
          className="group/sidebar fixed inset-y-0 z-30 flex md:z-10 md:pt-[3.75rem]"
          data-collapsed={collapsed ? `true` : `false`}
        >
          <Transition.Child
            // @ts-ignore
            as={Fragment}
            enter="transform transition duration-300 ease-in-out md:transition-none"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition duration-300 ease-in-out md:transition-none"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div
              className={cx(`flex-1`, {
                "!transition-none": wasDesktopRef.current
              })}
            >
              <div className="relative z-30 flex h-full w-[21rem] max-w-[calc(100vw-1.5rem)] flex-col border-r border-r-primary-light-gray bg-gray-100 md:w-60 md:group-data-[collapsed=true]/sidebar:w-20">
                <IconButton
                  className="absolute -right-3 top-4 z-10 hidden md:inline-flex"
                  aria-label={collapsed ? `Expand sidebar` : `Minimise sidebar`}
                  title={collapsed ? `Expand sidebar` : `Minimise sidebar`}
                  onClick={onToggleCollapsedClick}
                >
                  <svg
                    aria-hidden="true"
                    className="w-[1.375rem] h-[1.375rem] rotate-90 group-data-[collapsed=true]/sidebar:-rotate-90"
                  >
                    <use href="#sidebar-icon-chevron" />
                  </svg>
                </IconButton>

                <nav
                  className="relative flex-1 overflow-y-auto p-6 pr-16 md:px-2 md:pb-6 md:pt-4"
                  data-testid="sidebar-nav"
                  aria-label="Main"
                >
                  <button
                    type="button"
                    className="absolute top-4 right-4 md:hidden"
                    aria-label="Close sidebar"
                    title="Close sidebar"
                    onClick={onCloseClicked}
                  >
                    <CloseIcon className="w-8 h-8" aria-hidden="true" />
                  </button>

                  <ul>
                    {primaryItems.map(item => (
                      <SidebarItem
                        key={item.id}
                        {...item}
                        menuCollapsed={collapsed}
                        setMenuCollapsed={setCollapsed}
                      />
                    ))}
                  </ul>

                  {primaryItems.length && secondaryItems.length ? (
                    <hr className="my-5 mx-3 border-primary-light-gray" />
                  ) : null}

                  <ul>
                    {secondaryItems.map(item => (
                      <SidebarItem
                        key={item.id}
                        {...item}
                        menuCollapsed={collapsed}
                        setMenuCollapsed={setCollapsed}
                      />
                    ))}
                  </ul>
                </nav>
              </div>
            </div>
          </Transition.Child>
        </div>

        <Transition.Child
          // @ts-ignore
          as={Fragment}
          enter="transition-opacity duration-300 ease-linear md:transition-none"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-300 ease-linear md:transition-none"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={cx(
              `fixed inset-0 z-20 bg-black bg-opacity-25 backdrop-blur-sm md:hidden`,
              { "!transition-none": wasDesktopRef.current }
            )}
          />
        </Transition.Child>
      </Transition.Root>
    </>
  )
}

export default Sidebar
